import React, { useState } from "react";
import { CheckCircle2, Circle, Gavel, Loader2, Undo2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Avbockningsbar checklista från Regelmotorn (PBL/BBR).
 * Visas i CaseDetail för ärenden som skapats via /guide/wizard.
 */
export default function PermitChecklist({ caseId, items, meta, canEdit, onChange }) {
  const [busy, setBusy] = useState(null);
  if (!items || items.length === 0) return null;

  const done = items.filter((i) => i.checked).length;
  const pct = Math.round((done / items.length) * 100);

  const toggle = async (item) => {
    if (!canEdit) return;
    setBusy(item.item_id);
    try {
      const { data } = await api.post(`/crm/cases/${caseId}/checklist/toggle`, {
        item_id: item.item_id,
        checked: !item.checked,
      });
      onChange?.(data.items);
    } catch (err) {
      toast.error("Kunde inte uppdatera checklistan");
    } finally {
      setBusy(null);
    }
  };

  const undoAutoMatch = async (item, e) => {
    e.stopPropagation();
    if (!canEdit) return;
    if (!window.confirm(`Ångra AI-koppling för "${item.text.slice(0, 40)}${item.text.length > 40 ? "…" : ""}"? Itemet blir omarkerat.`)) return;
    setBusy(item.item_id);
    try {
      const { data } = await api.post(`/crm/cases/${caseId}/checklist/${item.item_id}/undo-auto-match`);
      onChange?.(data.items);
      toast.success("AI-koppling ångrad");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte ångra");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="permit-checklist">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-medium flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Gavel className="w-4 h-4 text-blue-700" />
            Handlingscheck (Regelmotor)
          </h3>
          {meta && (
            <div className="mt-1 text-xs text-zinc-500">
              Bedömning: <span className="font-medium text-zinc-700">{meta.verdict_label || meta.verdict}</span>
              {meta.project_type && <> · projekttyp <span className="font-medium">{meta.project_type}</span></>}
            </div>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-2xl font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>
            {done}/{items.length}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">klart</div>
        </div>
      </div>

      <div className="h-1.5 w-full bg-zinc-100 rounded-full overflow-hidden mb-4">
        <div
          className={`h-full transition-all duration-300 ${pct === 100 ? "bg-emerald-600" : "bg-blue-600"}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <ul className="space-y-1.5">
        {items.map((it) => {
          const isBusy = busy === it.item_id;
          return (
            <li
              key={it.item_id}
              className={`flex items-start gap-3 p-2.5 rounded-sm transition ${
                it.checked ? "bg-emerald-50/60" : "hover:bg-zinc-50"
              }`}
              data-testid={`checklist-item-${it.item_id}`}
            >
              <button
                onClick={() => toggle(it)}
                disabled={!canEdit || isBusy}
                data-testid={`checklist-toggle-${it.item_id}`}
                className={`flex-shrink-0 mt-0.5 ${canEdit ? "cursor-pointer" : "cursor-default"}`}
              >
                {isBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />
                ) : it.checked ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 stroke-[2]" />
                ) : (
                  <Circle className="w-4 h-4 text-zinc-300 stroke-[1.5]" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`text-sm ${it.checked ? "text-zinc-500 line-through" : "text-zinc-800"}`}>
                  {it.text}
                </div>
                {it.checked && it.checked_by && (
                  <div className="text-[11px] text-zinc-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
                    {it.auto_matched_doc_id && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 rounded-sm border border-purple-200"
                        data-testid={`ai-match-badge-${it.item_id}`}>
                        ⚡ AI-kopplad
                      </span>
                    )}
                    <span>Markerat av {it.checked_by}
                    {it.checked_at && ` · ${new Date(it.checked_at).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })}`}
                    </span>
                    {it.auto_matched_doc_id && canEdit && (
                      <button onClick={(e) => undoAutoMatch(it, e)} data-testid={`undo-auto-match-${it.item_id}`}
                        className="inline-flex items-center gap-0.5 text-[11px] text-purple-700 hover:text-purple-900 hover:underline">
                        <Undo2 className="w-3 h-3" /> Ångra
                      </button>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {!canEdit && (
        <div className="mt-3 text-[11px] text-zinc-400 italic">
          Endast handläggare och sökande kan markera handlingar som klara.
        </div>
      )}
    </div>
  );
}
